import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Spinner } from "@npco/zeller-design-system";
import type * as Core from "@zeller-public/payments-sdk-core";
import { request as grequest } from "graphql-request";
import {
	GraphQLClient as ApiClient,
	ClientError,
	type RequestDocument,
	type RequestOptions,
} from "graphql-request";
import { ErrorADT, safe } from "pb.safe";
import type { Identity } from "pb.types";
import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type FunctionComponent,
	useCallback,
} from "react";

import { $, query } from "../../../../schema.dev-sdk-api-appsync.generated";
import { Client as WsClient } from "../../../@lib-appsync-ws";
import { login, refreshTokens, resolveTokens } from "../../../@lib-auth";
import { logger } from "../../../shared/logger";
import { suspend } from "../../../shared/suspend";
import * as transport from "../../../transport";
import { AuthenticationPrompt } from "./authentication-prompt";
import { AuthenticationRefreshFailure } from "./authentication-refresh-failure";
import { Layout } from "./layout";
import { Prompt } from "./prompt";
import { RouteProvider } from "./route-provider";
import type { RouteContext } from "./shared/route-context";

const apiUrl = import.meta.env["VITE_API_URL"];

// A blackholed network never settles the fetch; treat it as offline.
const HEALTHCHECK_TIMEOUT = 10000; // 10 seconds

type Props<TQueryType extends Core.Protocol.Request_Query["query"]["$type"]> =
	Identity<{
		query: Extract<Core.Protocol.Request_Query["query"], { $type: TQueryType }>;
		headers: Core.Protocol.Request_Query["headers"];
	}>;

export namespace Route {
	export type Request<
		TQueryType extends Core.Protocol.Request_Query["query"]["$type"] =
			Core.Protocol.Request_Query["query"]["$type"],
	> = {
		query: Extract<Core.Protocol.Request_Query["query"], { $type: TQueryType }>;
		headers: Core.Protocol.Request_Query_headers;
		tokens: TokensResolveFn;
		interactive: boolean;
	};

	export type Tokens = Required<
		Pick<
			Core.Protocol.Request_Query_headers,
			"authAccessToken" | "authRefreshToken" | "authIdToken"
		>
	>;

	export type TokensResolveFn = () => Promise<Tokens | undefined>;
	export type TokensRefreshFn = () => Promise<Tokens | void>;
}

function getNowDate() {
	return new Date();
}

export function Route<
	TQueryType extends Core.Protocol.Request_Query["query"]["$type"],
>(
	{
		interactive: _interactive = true,
	}: {
		query: TQueryType;
		/**
		 * If `true` (default) then UI is assumed to be shown.
		 * If `false` then UI feedback will be automatically resolved.
		 */
		interactive?:
			| boolean
			| ((
					query: Extract<
						Core.Protocol.Request_Query["query"],
						{ $type: TQueryType }
					>,
			  ) => boolean | undefined);
	},
	Component: FunctionComponent<Route.Request<TQueryType>>,
): FunctionComponent<Props<TQueryType>> & {
	Component: FunctionComponent<Route.Request<TQueryType>>;
} {
	function RouteComponent(request: Props<TQueryType>) {
		const headers = useMemo(() => request.headers ?? {}, [request]);
		const $tokens = useRef<Partial<Route.Tokens> | undefined>(headers);

		const interactive: boolean = useMemo(
			() =>
				typeof _interactive === "function"
					? (_interactive(
							request.query as Extract<
								Core.Protocol.Request_Query["query"],
								{ $type: TQueryType }
							>,
						) ?? true)
					: _interactive,
			[request.query],
		);

		//

		const tokensResolveFn = useCallback<Route.TokensResolveFn>(() => {
			const tokens = resolveTokens($tokens.current);
			return Promise.resolve(tokens instanceof Error ? undefined : tokens);
		}, []);

		const tokensRefresh = useCallback<Route.TokensRefreshFn>(async () => {
			const tokens = await refreshTokens($tokens.current);

			if (tokens instanceof Error) {
				// transient failures keep the tokens so the next request retries
				if (tokens.type === "RefreshTokenUnavailable") {
					logger.warn("tokens refresh: unavailable so keeping config");
					return;
				}

				logger.warn("tokens refresh: failed so flushing config");

				// refresh token failure clears configuration so we are forced
				// to login again on subsequent request

				// undefined values are stripped on JSON.stringify so must be
				// passed as empty strings, do not use reset on setup required
				// as also removes device id!
				transport.Config({
					authAccessToken: "",
					authRefreshToken: "",
					authIdToken: "",
				});

				return void setAuthenticated(ErrorADT("AuthenticationRefreshFailure"));
			}

			logger.info("tokens refresh: tokens refreshed and returning config");

			$tokens.current = tokens;
			transport.Config(tokens);

			return tokens;
		}, []);

		const $tokensRefresh = useRef<
			ReturnType<Route.TokensRefreshFn> | undefined
		>(undefined);

		// concurrent 401s share one exchange, a rotated refresh token is single use
		const tokensRefreshFn = useCallback<Route.TokensRefreshFn>(() => {
			$tokensRefresh.current ??= tokensRefresh().finally(() => {
				$tokensRefresh.current = undefined;
			});
			return $tokensRefresh.current;
		}, [tokensRefresh]);

		//

		const [authenticated, setAuthenticated] = useState<
			boolean | ErrorADT<"AuthenticationRefreshFailure">
		>();

		useEffect(() => {
			void (async () => {
				const tokens = await tokensResolveFn();
				if (!tokens) {
					if (!interactive) {
						return void AuthenticationFailure_onClose();
					}
					return void setAuthenticated(false);
				}
				return void setAuthenticated(true);
			})();
		}, [request, tokensResolveFn, interactive]);

		//

		const apiClient = useMemo<RouteContext["apiClient"]>(() => {
			const apiClient = new ApiClient(apiUrl);
			const _request = apiClient.request.bind(apiClient);
			apiClient.request = async (...args) => {
				const storedTokens = await tokensResolveFn();

				if (storedTokens) {
					apiClient.setHeader("Authorization", storedTokens.authAccessToken);
				}

				const response = await safe(() =>
					_request(
						...(args as unknown as Parameters<typeof apiClient.request>),
					),
				);

				if (response instanceof Error && response instanceof ClientError) {
					const isUnauthenticated = response.response.status === 401;

					if (isUnauthenticated) {
						logger.warn("api client: unauthenticated error");

						const tokensRefreshResponse = await tokensRefreshFn();

						// Return the response of the initial failed request if token
						// refresh failed.
						if (!tokensRefreshResponse) {
							return response;
						}

						apiClient.setHeader(
							"Authorization",
							tokensRefreshResponse.authAccessToken,
						);

						const retryResponse = await safe(() =>
							_request(
								...(args as unknown as Parameters<typeof apiClient.request>),
							),
						);

						if (retryResponse instanceof Error) {
							logger.error("api client: retry request response error", {
								"zeller.api.operationName": getOperationName(args[0]),
								"zeller.api.errorName": retryResponse.name,
								"zeller.api.errorMessage": retryResponse.message,
								"zeller.api.errorStack": retryResponse.stack,
								"zeller.api.errorCause": retryResponse.cause,
							});
						}

						return retryResponse;
					}
				}

				if (response instanceof Error) {
					logger.error("api client: response error", {
						"zeller.api.operationName": getOperationName(args[0]),
						"zeller.api.errorName": response.name,
						"zeller.api.errorMessage": response.message,
						"zeller.api.errorStack": response.stack,
						"zeller.api.errorCause": response.cause,
					});
				}

				return response;
			};

			return apiClient;
		}, [tokensResolveFn, tokensRefreshFn]);

		const healthcheck = useCallback(
			async (
				deviceId: string,
			): Promise<undefined | ErrorADT<"NetworkError">> => {
				const storedTokens = await tokensResolveFn();

				const response = await safe(() =>
					grequest({
						url: apiUrl,
						document: query("getDeviceSettings", (q) => [
							q.getDeviceSettings({ deviceUuid: $("deviceUuid") }, (c) => [
								c.id,
							]),
						]),
						requestHeaders: {
							Authorization: storedTokens?.authAccessToken ?? "",
						},
						variables: { deviceUuid: deviceId },
						signal: AbortSignal.timeout(HEALTHCHECK_TIMEOUT),
					}),
				);

				if (response instanceof ClientError) {
					const isUnauthenticated = response.response.status === 401;

					if (isUnauthenticated) {
						const tokensRefreshResponse = await tokensRefreshFn();

						if (!tokensRefreshResponse) {
							return ErrorADT("NetworkError");
						}
					}
					return undefined;
				}

				// Anything else (offline, DNS, timeout) never reached the api.
				return response instanceof Error ? ErrorADT("NetworkError") : undefined;
			},
			[tokensResolveFn, tokensRefreshFn],
		);

		const wsClient = useMemo(
			() =>
				WsClient({
					origin: `wss://${new URL(apiUrl).host}`,
					getToken: () =>
						tokensResolveFn().then((tokens) => tokens?.authAccessToken),
				}),
			[tokensResolveFn],
		);

		// omitted error adt type check until we have multiple types
		if (authenticated instanceof Error) {
			return (
				<AuthenticationRefreshFailure
					onClose={AuthenticationFailure_onRefresh}
					onLogin={() => {
						setAuthenticated(undefined);
						void login(request);
					}}
				/>
			);
		}

		if (!authenticated) {
			if (authenticated === false) {
				return (
					<Layout onClose={AuthenticationFailure_onClose}>
						<AuthenticationPrompt onLogin={() => login(request)} />
					</Layout>
				);
			}

			return (
				<Layout>
					<Prompt image={<Spinner size="lg" />} />
				</Layout>
			);
		}

		return (
			<RouteProvider {...{ apiClient, wsClient, getNowDate, healthcheck }}>
				<Component
					query={request.query}
					headers={headers}
					tokens={tokensResolveFn}
					interactive={interactive}
				/>
			</RouteProvider>
		);
	}
	return Object.assign(RouteComponent, { Component });
}

function getOperationName(
	document:
		| RequestDocument
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		| TypedDocumentNode<any, any>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		| RequestOptions<any, any>,
): string | undefined {
	if (
		typeof document !== "string" &&
		"definitions" in document &&
		document.definitions[0]?.kind === "OperationDefinition"
	) {
		return document.definitions[0].name?.value;
	}

	return undefined;
}

function AuthenticationFailure_onClose(): Promise<void> {
	transport.Resolve.Error("Setup Required", {
		message: "Authentication failure",
	});
	return suspend();
}

function AuthenticationFailure_onRefresh(): Promise<void> {
	transport.Resolve.Error("Setup Required", {
		message: "Authentication refresh failure",
	});
	return suspend();
}
